import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
const _excluded = ["display", "height", "width", "minWidth", "maxWidth", "margin", "onDropAccepted", "onDropRejected", "styles"];
var _dec, _dec2, _dec3, _class, _FileDrop;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import keycode from 'keycode';
import { FormFieldMessages } from '@instructure/ui-form-field';
import { View } from '@instructure/ui-view';
import { testable } from '@instructure/ui-testable';
import { callRenderProp, passthroughProps, getInteraction, withDeterministicId } from '@instructure/ui-react-utils';
import { accepts, getAcceptList } from './utils/accepts';
import { getEventFiles } from './utils/getEventFiles';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
function keyEventIsClickButton(e) {
  return e.keyCode === keycode.codes.space || e.keyCode === keycode.codes.enter;
}

/**
---
category: components
---
**/
let FileDrop = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_FileDrop = class FileDrop extends Component {
  constructor(props) {
    super(props);
    this.makeStyleProps = () => {
      return {
        functionallyDisabled: this.functionallyDisabled,
        visuallyDisabled: this.interaction === 'disabled',
        dragRejected: this.state.isDragRejected || this.invalid,
        dragAccepted: this.state.isDragAccepted
      };
    };
    this.state = {
      isDragAccepted: false,
      isDragRejected: false,
      isFocused: false,
      isFileBrowserDisplayed: false
    };
    this.enterCounter = 0;
    this.fileInputEl = void 0;
    this.defaultId = null;
    this.messagesId = '';
    this.ref = null;
    this.handleElementRef = el => {
      this.ref = el;
    };
    this.handleDragEnter = e => {
      var _this$props$onDragEnt, _this$props;
      e.preventDefault();
      // Count the dropzone and any children that are entered.
      this.enterCounter += 1;
      // Don't trigger onDragEnter for each children after the first one
      if (this.enterCounter > 1) {
        return;
      }
      const allFilesAccepted = this.allFilesAccepted(this.getDataTransferItems(e));
      this.setState({
        isDragAccepted: allFilesAccepted,
        isDragRejected: !allFilesAccepted
      });
      (_this$props$onDragEnt = (_this$props = this.props).onDragEnter) === null || _this$props$onDragEnt === void 0 ? void 0 : _this$props$onDragEnt.call(_this$props, e);
    };
    this.handleDragOver = e => {
      var _this$props$onDragOve, _this$props2;
      e.preventDefault();
      e.stopPropagation();
      try {
        const event = e;
        event.dataTransfer.dropEffect = 'copy';
      } catch (err) {
        // continue regardless of error
      }
      (_this$props$onDragOve = (_this$props2 = this.props).onDragOver) === null || _this$props$onDragOve === void 0 ? void 0 : _this$props$onDragOve.call(_this$props2, e);
      return false;
    };
    this.handleDragLeave = e => {
      var _this$props$onDragLea, _this$props3;
      e.preventDefault();
      this.enterCounter -= 1;
      // Only deactivate once the dropzone and all children was left
      if (this.enterCounter > 0) {
        return;
      }
      this.setState({
        isDragAccepted: false,
        isDragRejected: false
      });
      (_this$props$onDragLea = (_this$props3 = this.props).onDragLeave) === null || _this$props$onDragLea === void 0 ? void 0 : _this$props$onDragLea.call(_this$props3, e);
    };
    this.handleChange = e => {
      const _this$props4 = this.props,
        onDrop = _this$props4.onDrop,
        onDropAccepted = _this$props4.onDropAccepted,
        onDropRejected = _this$props4.onDropRejected,
        shouldEnablePreview = _this$props4.shouldEnablePreview;
      const fileList = this.getDataTransferItems(e, shouldEnablePreview);
      const _this$parseFiles = this.parseFiles(fileList),
        _this$parseFiles2 = _slicedToArray(_this$parseFiles, 2),
        accepted = _this$parseFiles2[0],
        rejected = _this$parseFiles2[1];
      e.preventDefault();
      this.enterCounter = 0;
      // When dropping a file the browser does not populate the input's
      // 'files' property, so we need to do it manually. This will also populate
      // the input's 'value' prop, which contains the first file's path
      if (accepted.length > 0 && 'dataTransfer' in e) {
        this.fileInputEl.files = e.dataTransfer.files;
      }
      onDrop && onDrop(accepted, rejected, e);
      if (rejected.length > 0 && onDropRejected) {
        onDropRejected(rejected, e);
      }
      if (accepted.length > 0 && onDropAccepted) {
        onDropAccepted(accepted, e);
      }
      this.setState({
        isDragAccepted: false,
        isDragRejected: false,
        isFileBrowserDisplayed: false
      });
    };
    this.fileAccepted = file => {
      if (this.props.accept) return accepts(file, this.props.accept);else return true;
    };
    this.handleRef = el => {
      this.fileInputEl = el;
      if (typeof this.props.inputRef === 'function') {
        this.props.inputRef(el);
      }
    };
    this.handleBlur = () => {
      this.setState({
        isFocused: false,
        isFileBrowserDisplayed: false
      });
    };
    this.handleFocus = () => {
      this.setState({
        isFocused: true,
        isFileBrowserDisplayed: false
      });
    };
    this.handleClick = e => {
      var _this$fileInputEl;
      if (this.fileInputEl.value && this.props.shouldAllowRepeats) {
        this.fileInputEl.value = null;
      }
      // focus the input (because FF won't)
      (_this$fileInputEl = this.fileInputEl) === null || _this$fileInputEl === void 0 ? void 0 : _this$fileInputEl.focus();
      this.props.onClick(e);
      this.setState({
        isFileBrowserDisplayed: true
      });
    };
    this.handleKeyDown = e => {
      if (this.state.isFocused && keyEventIsClickButton(e)) {
        if (this.props.shouldAllowRepeats) {
          this.fileInputEl.value = null;
        }
      }
    };
    this.handleKeyUp = e => {
      // This is to handle the case where ESC is pressed inside a Dialog so that
      // closing the file browser dialog doesn't also close the Dialog.
      if (e.keyCode === keycode.codes.esc && this.state.isFileBrowserDisplayed) {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        this.setState({
          isFileBrowserDisplayed: false
        });
      }
    };
    this.defaultId = props.deterministicId();
    this.messagesId = props.deterministicId('FileDrop-messages');
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props5;
    (_this$props$makeStyle = (_this$props5 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props5, this.makeStyleProps());
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props6;
    (_this$props$makeStyle2 = (_this$props6 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props6, this.makeStyleProps());
  }
  convertToFile(fileLikeItem) {
    if (fileLikeItem instanceof DataTransferItem) return fileLikeItem.getAsFile();else return fileLikeItem;
  }
  get functionallyDisabled() {
    return this.interaction === 'disabled' || this.interaction === 'readonly';
  }
  get interaction() {
    return getInteraction({
      props: this.props
    });
  }
  get hasMessages() {
    return this.props.messages ? this.props.messages.length > 0 : false;
  }
  get invalid() {
    if (this.hasMessages) {
      return this.props.messages.findIndex(message => {
        return message.type === 'error' || message.type === 'newError';
      }) >= 0;
    }
    return false;
  }
  getDataTransferItems(e, shouldEnablePreview) {
    let list = [];
    Array.from(getEventFiles(e, this.fileInputEl)).forEach(file => {
      const fileObj = this.convertToFile(file);
      if (fileObj) list.push(fileObj);
    });
    if (list.length > 1) {
      list = this.props.shouldAllowMultiple ? list : [list[0]];
    }
    if (shouldEnablePreview) {
      return list.map(file => Object.assign(file, {
        preview: window.URL.createObjectURL(file)
      }));
    }
    return list;
  }
  parseFiles(fileList) {
    const accepted = [];
    const rejected = [];
    fileList.forEach(file => {
      const fileObj = this.convertToFile(file);
      if (fileObj && this.fileAccepted(fileObj) && this.fileMatchSize(fileObj)) {
        accepted.push(file);
      } else {
        rejected.push(file);
      }
    });
    return [accepted, rejected];
  }
  fileMatchSize(file) {
    return file.size <= this.props.maxSize && file.size >= this.props.minSize;
  }
  allFilesAccepted(files) {
    return files.every(this.fileAccepted);
  }
  acceptStr() {
    const accept = this.props.accept;
    return accept ? getAcceptList(accept).join(',') : void 0;
  }
  renderLabel() {
    const renderLabel = this.props.renderLabel;
    return callRenderProp(renderLabel, {
      isDragAccepted: this.state.isDragAccepted,
      isDragRejected: this.state.isDragRejected,
      interaction: this.interaction
    });
  }
  render() {
    var _this$props$styles, _styles$fileDropLayou, _this$props$styles2, _this$props$styles3, _this$props$styles4;
    const _this$props7 = this.props,
      display = _this$props7.display,
      height = _this$props7.height,
      width = _this$props7.width,
      minWidth = _this$props7.minWidth,
      maxWidth = _this$props7.maxWidth,
      margin = _this$props7.margin,
      onDropAccepted = _this$props7.onDropAccepted,
      onDropRejected = _this$props7.onDropRejected,
      styles = _this$props7.styles,
      props = _objectWithoutProperties(_this$props7, _excluded);
    const id = this.props.id || this.defaultId;
    const focusColor = this.state.isDragRejected || this.invalid ? 'danger' : void 0;
    return _jsxs(View, {
      display: display,
      position: "relative" // contain visually hidden file input element
      ,
      width: width,
      minWidth: minWidth,
      maxWidth: maxWidth,
      margin: margin,
      height: height,
      elementRef: this.handleElementRef,
      children: [_jsx("label", {
        css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.fileDropLabel,
        htmlFor: id,
        onDragEnter: this.handleDragEnter,
        onDragOver: this.handleDragOver,
        onDragLeave: this.handleDragLeave,
        onDrop: this.handleChange,
        children: _jsx(View, {
          borderRadius: String(styles === null || styles === void 0 ? void 0 : (_styles$fileDropLayou = styles.fileDropLayout) === null || _styles$fileDropLayou === void 0 ? void 0 : _styles$fileDropLayou.borderRadius),
          display: "block",
          position: "relative",
          withFocusOutline: this.state.isFocused,
          focusColor: focusColor,
          height: height,
          children: _jsx("span", {
            css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.fileDropLabelContent,
            children: _jsx("span", {
              css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.fileDropLayout,
              children: _jsx(View, {
                height: height,
                children: this.renderLabel()
              })
            })
          })
        })
      }), _jsx("input", {
        ...passthroughProps(props),
        onClick: this.handleClick,
        type: "file",
        css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.fileDropInput,
        id: id,
        ref: this.handleRef,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        onKeyDown: this.handleKeyDown,
        onKeyUp: this.handleKeyUp,
        multiple: this.props.shouldAllowMultiple,
        accept: this.acceptStr(),
        onChange: this.handleChange,
        "aria-describedby": this.hasMessages ? this.messagesId : void 0,
        "aria-invalid": this.invalid,
        disabled: this.functionallyDisabled
      }), this.hasMessages ? _jsx(View, {
        display: "block",
        margin: "small 0 0",
        children: _jsx(FormFieldMessages, {
          id: this.messagesId,
          messages: this.props.messages
        })
      }) : null]
    });
  }
}, _FileDrop.displayName = "FileDrop", _FileDrop.componentId = 'FileDrop', _FileDrop.propTypes = propTypes, _FileDrop.allowedProps = allowedProps, _FileDrop.defaultProps = {
  onClick: function (_e) {},
  shouldEnablePreview: false,
  shouldAllowMultiple: false,
  shouldAllowRepeats: true,
  maxSize: Infinity,
  minSize: 0,
  // Leave interaction default undefined so that `disabled` and `readOnly` can also be supplied
  messages: [],
  display: 'block'
}, _FileDrop)) || _class) || _class) || _class);
export default FileDrop;
export { FileDrop };